import React from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components-latest/primitives/Button'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import Text from '@/components-latest/primitives/Text'
import CycleInfo from '@/components-latest/ui/BillingPage/CycleInfo'
import { IBillingCycle } from '@/ducks/billing/billing.types'

import Styles from './BillingCyclesList.module.scss'

const BillingDetailsIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.83913 0.666687H10.1609C10.6975 0.666679 11.1404 0.666672 11.5012 0.69615C11.876 0.726767 12.2204 0.792476 12.544 0.957337C13.0457 1.213 13.4537 1.62095 13.7094 2.12271C13.8742 2.44627 13.9399 2.79074 13.9705 3.16547C14 3.52626 14 3.96917 14 4.50581V11.4942C14 12.0309 14 12.4738 13.9705 12.8346C13.9399 13.2093 13.8742 13.5538 13.7094 13.8773C13.4537 14.3791 13.0457 14.787 12.544 15.0427C12.2204 15.2076 11.876 15.2733 11.5012 15.3039C11.1404 15.3334 10.6975 15.3334 10.1609 15.3334H5.83912C5.30248 15.3334 4.85958 15.3334 4.49878 15.3039C4.12405 15.2733 3.77958 15.2076 3.45603 15.0427C2.95426 14.787 2.54631 14.3791 2.29065 13.8773C2.12579 13.5538 2.06008 13.2093 2.02946 12.8346C1.99998 12.4738 1.99999 12.0309 2 11.4942V4.50582C1.99999 3.96917 1.99998 3.52627 2.02946 3.16547C2.06008 2.79074 2.12579 2.44627 2.29065 2.12271C2.54631 1.62095 2.95426 1.213 3.45603 0.957337C3.77958 0.792476 4.12405 0.726767 4.49878 0.69615C4.85958 0.666672 5.30249 0.666679 5.83913 0.666687ZM4.60736 2.02506C4.31508 2.04894 4.16561 2.09222 4.06135 2.14535C3.81046 2.27318 3.60649 2.47715 3.47866 2.72803C3.42553 2.8323 3.38225 2.98177 3.35837 3.27405C3.33385 3.57411 3.33333 3.9623 3.33333 4.53335V11.4667C3.33333 12.0377 3.33385 12.4259 3.35837 12.726C3.38225 13.0183 3.42553 13.1677 3.47866 13.272C3.60649 13.5229 3.81046 13.7269 4.06135 13.8547C4.16561 13.9078 4.31508 13.9511 4.60736 13.975C4.90742 13.9995 5.29561 14 5.86667 14H10.1333C10.7044 14 11.0926 13.9995 11.3926 13.975C11.6849 13.9511 11.8344 13.9078 11.9387 13.8547C12.1895 13.7269 12.3935 13.5229 12.5213 13.272C12.5745 13.1677 12.6178 13.0183 12.6416 12.726C12.6661 12.4259 12.6667 12.0377 12.6667 11.4667V4.53335C12.6667 3.9623 12.6661 3.57411 12.6416 3.27405C12.6178 2.98177 12.5745 2.8323 12.5213 2.72803C12.3935 2.47715 12.1895 2.27318 11.9387 2.14535C11.8344 2.09222 11.6849 2.04894 11.3926 2.02506C11.0926 2.00054 10.7044 2.00002 10.1333 2.00002H5.86667C5.29561 2.00002 4.90742 2.00054 4.60736 2.02506ZM4.66667 4.66669C4.66667 4.2985 4.96514 4.00002 5.33333 4.00002H10.6667C11.0349 4.00002 11.3333 4.2985 11.3333 4.66669C11.3333 5.03488 11.0349 5.33335 10.6667 5.33335H5.33333C4.96514 5.33335 4.66667 5.03488 4.66667 4.66669ZM4.66667 7.33335C4.66667 6.96517 4.96514 6.66669 5.33333 6.66669H9.33333C9.70152 6.66669 10 6.96517 10 7.33335C10 7.70155 9.70152 8.00002 9.33333 8.00002H5.33333C4.96514 8.00002 4.66667 7.70155 4.66667 7.33335ZM4.66667 10C4.66667 9.63183 4.96514 9.33336 5.33333 9.33336H6.66667C7.03486 9.33336 7.33333 9.63183 7.33333 10C7.33333 10.3682 7.03486 10.6667 6.66667 10.6667H5.33333C4.96514 10.6667 4.66667 10.3682 4.66667 10Z"
        fill="#FB2576"
      />
    </svg>
  )
}

const BillingCyclesList = ({
  type,
  cycles,
  onDetailsClick,
  showTitle = true
}: {
  cycles: IBillingCycle[]
  type: 'paid' | 'unpaid' | 'unreceived'
  onDetailsClick?: (
    cycle: IBillingCycle | null,
    type: 'paid' | 'unpaid'
  ) => void
  showTitle?: boolean
}) => {
  const { t } = useTranslation()
  const cycleInfoType = type === 'unreceived' ? 'unpaid' : type

  if (!cycles || cycles.length === 0) return null

  return (
    <div className={Styles.wrapper}>
      {showTitle ? (
        <Text
          variant="nextSubtitle"
          weight="extraBold"
          className={Styles.title}>
          {t(`billing.cycle-list.title-${type}`)}
        </Text>
      ) : null}
      <div className={Styles.list}>
        {cycles.map(cycle => (
          <ContentBlock className={Styles.cycleItem} key={cycle.billing_cycle}>
            <CycleInfo
              cycle={cycle}
              type={cycleInfoType}
              headerAction={
                <Button
                  className={Styles.detailsButton}
                  variant="transparentOutlined"
                  size="small"
                  shape="rounded"
                  icon={<BillingDetailsIcon className={Styles.detailsIcon} />}
                  onClick={() => {
                    onDetailsClick?.(cycle, cycleInfoType)
                  }}>
                  {t(`billing.cycle-list.details-link-${type}`)}
                </Button>
              }
            />
          </ContentBlock>
        ))}
      </div>
    </div>
  )
}

export default BillingCyclesList
